import { forwardRef } from 'react';
import type { PropsWithoutRef } from 'react';
import styled, { css } from 'styled-components';

import type { RefElement } from '@pega/cosmos-react-core';
import { defaultThemeProp, FileList, useConsolidatedRef } from '@pega/cosmos-react-core';
import BareButton from '@pega/cosmos-react-core/lib/components/Button/BareButton';

import { StyledOpenGraphAnchor } from '../OpenGraphPreview/OpenGraphPreview.styles';
import OpenGraphPreview from '../OpenGraphPreview';

import type { FeedAttachmentsProps } from './Feed.types';

export const StyledImagePreview = styled(BareButton)(({ theme }) => {
  return css`
    line-height: 0;
    width: fit-content;
    border-radius: calc(0.5 * ${theme.base['border-radius']});

    & + & {
      margin: 0;
    }

    &:focus-visible {
      box-shadow: ${theme.components.button['focus-shadow']};
    }

    img {
      border-radius: calc(0.5 * ${theme.base['border-radius']});
      object-fit: contain;
      max-height: 10rem;
      max-width: 100%;
    }
  `;
});

StyledImagePreview.defaultProps = defaultThemeProp;

export const StyledAttachmentsRegion = styled.div<FeedAttachmentsProps>(({ variant, theme }) => {
  return css`
    margin: 0.5rem calc(5 * 0.5rem) 0 calc(${variant === 'post' ? 6 : 5} * 0.5rem);

    ${StyledOpenGraphAnchor} {
      margin-block-end: calc(${theme.base.spacing} / 2);
    }
  `;
});

StyledAttachmentsRegion.defaultProps = defaultThemeProp;

const FeedAttachments = forwardRef<
  RefElement<FeedAttachmentsProps>,
  PropsWithoutRef<FeedAttachmentsProps>
>(function FeedAttachments(props, ref) {
  const { variant, attachments = [], openGraphData, disabled, onDownloadAll } = props;

  const containerRef = useConsolidatedRef(ref);

  return openGraphData || attachments.length > 0 ? (
    <StyledAttachmentsRegion variant={variant} ref={containerRef}>
      {openGraphData && <OpenGraphPreview data={openGraphData} containerRef={containerRef} />}
      <FileList
        type='item'
        items={attachments}
        disabled={disabled}
        onDownloadAll={onDownloadAll}
        ref={ref}
      />
    </StyledAttachmentsRegion>
  ) : null;
});

export default FeedAttachments;
